<template>
	<el-dialog
		:visible.sync="visible"
		:before-close="onClose"
		width="880px"
		top="5vh"
		custom-class="model-select-dialog"
		:append-to-body="true"
	>
		<div slot="title" class="dialog-header">
			<span class="dialog-title">选择 AI 模型</span>
			<el-input
				v-model="keyword"
				placeholder="搜索模型名称..."
				size="small"
				prefix-icon="el-icon-search"
				clearable
				class="search-input"
				@input="onSearchInput"
			/>
		</div>

		<div class="dialog-body" v-loading="loading">
			<!-- 场景筛选 -->
			<div class="scene-tabs">
				<div
					v-for="s in SCENES"
					:key="s.key"
					:class="['scene-tab', { active: activeScene === s.key }]"
					@click="switchScene(s.key)"
				>
					<span class="scene-icon">{{ s.icon }}</span>
					<span>{{ s.label }}</span>
					<span class="scene-count" v-if="s.key !== 'all' && sceneCount(s.key) > 0">{{ sceneCount(s.key) }}</span>
				</div>
			</div>

			<!-- 场景说明条 -->
			<div class="scene-hint" v-if="activeScene !== 'all' && currentScene">
				{{ currentScene.hint }}
			</div>

			<!-- 最近使用 -->
			<template v-if="recentModels.length > 0 && !keyword && activeScene === 'all'">
				<div class="section-label">
					<i class="el-icon-time"></i> 最近使用
				</div>
				<div class="recent-row">
					<div
						v-for="m in recentModels"
						:key="'r-' + m.guid"
						:class="['recent-chip', { active: selectedGuid === m.guid }]"
						@click="onSelect(m)"
					>
						<span class="chip-dot" :style="{ background: vendorColor(m.modelVendorSign) }"></span>
						<span class="chip-name">{{ m.modelName }}</span>
						<i class="el-icon-check chip-check" v-if="selectedGuid === m.guid"></i>
						<i
							class="el-icon-close chip-remove"
							@click.stop="removeRecent(m.guid)"
						></i>
					</div>
				</div>
				<div class="section-label mt12">
					<i class="el-icon-set-up"></i> 全部模型
				</div>
			</template>

			<!-- 无结果 -->
			<div class="empty-tip" v-if="displayModels.length === 0 && !loading">
				<i class="el-icon-search"></i>
				<p v-if="keyword">未找到含"{{ keyword }}"的模型</p>
				<p v-else>当前分类暂无可用模型</p>
			</div>

			<!-- 模型卡片 -->
			<div class="model-grid" v-else>
				<div
					v-for="item in displayModels"
					:key="item.guid"
					:class="['model-card', { active: selectedGuid === item.guid }]"
					@click="onSelect(item)"
				>
					<i class="el-icon-check check-mark" v-if="selectedGuid === item.guid"></i>

					<!-- 卡片头：厂商色点 + 模型名 -->
					<div class="card-head">
						<span class="vendor-dot" :style="{ background: vendorColor(item.modelVendorSign) }"></span>
						<span class="card-name">{{ item.modelName }}</span>
					</div>
					<div class="card-company">{{ item.companyName || vendorLabel(item.modelVendorSign) }}</div>

					<!-- 能力标签 -->
					<div class="cap-row">
						<span class="cap cap-reasoning1" v-if="item.isReasoning === 1">🧠 固定推理</span>
						<span class="cap cap-reasoning2" v-if="item.isReasoning === 2">💭 可选思考</span>
						<span class="cap cap-vision" v-if="item.isImageRecognition === 1">👁 识图</span>
						<span class="cap cap-fast" v-if="isFastModel(item)">⚡ 快速</span>
						<span class="cap cap-free" v-if="item.usePrice === 0">🆓 免费</span>
					</div>

					<!-- 适合场景 -->
					<div class="card-usecase">
						<span>适合：</span>{{ getUseCases(item) }}
					</div>

					<!-- 描述 -->
					<div class="card-desc">{{ item.modelDesc || defaultDesc(item) }}</div>

					<!-- 底部：点数 -->
					<div class="card-footer">
						<span class="price-free" v-if="item.usePrice === 0">
							<i class="el-icon-unlock"></i> 免费使用
						</span>
						<span class="price-paid" v-else>
							<i class="el-icon-coin"></i> {{ item.usePrice }} 点/次
						</span>
					</div>
				</div>
			</div>
		</div>
	</el-dialog>
</template>

<script>
import { getAiVendorListApi, getAiVendorModelListApi } from '@/apis/chat';

const SCENES = [
	{ key: 'all',       icon: '🤖', label: '全部',   hint: '' },
	{ key: 'reasoning', icon: '🧠', label: '深度推理', hint: '支持链式思考，适合数学、逻辑分析、复杂问题拆解' },
	{ key: 'vision',    icon: '👁',  label: '识图',   hint: '可理解图片内容，适合图表解读、截图分析、视觉问答' },
	{ key: 'free',      icon: '🆓', label: '免费',   hint: '无需消耗点数，适合日常轻量使用和模型试用' },
	{ key: 'fast',      icon: '⚡', label: '快速响应', hint: '响应速度快、延迟低，适合简单问答和高频对话场景' },
];

const VENDOR_LABELS = {
	openrouter: 'OpenRouter',
	deepseek:   'DeepSeek',
	zhipu:      '智谱',
	azure:      'Azure',
	openai:     'OpenAI',
	closeai:    'CloseAI',
};

const VENDOR_COLORS = {
	openrouter: '#8b5cf6',
	deepseek:   '#1d4ed8',
	zhipu:      '#059669',
	openai:     '#10b981',
	azure:      '#0078d4',
	closeai:    '#7c3aed',
};

const FAST_PATTERN = /flash|mini|haiku|turbo|fast|lite|nano|quick|instant/i;

const RECENT_KEY    = 'dc_recent_model_guids';
const MAX_RECENT    = 3;

export default {
	name: 'model-select-dialog',

	props: {
		visible:      { type: Boolean, default: false },
		selectedGuid: { type: String,  default: '' },
	},

	data() {
		return {
			SCENES,
			loading:      false,
			activeScene:  'all',
			keyword:      '',
			allModels:    [],
			recentModels: [],
			loaded:       false,
		};
	},

	computed: {
		currentScene() {
			return SCENES.find(s => s.key === this.activeScene) || null;
		},

		displayModels() {
			// 只展示文字模型
			let list = this.allModels.filter(m => m.modelType === 'text');

			// 场景筛选
			if (this.activeScene !== 'all') {
				list = list.filter(m => this.matchesScene(m, this.activeScene));
			}

			// 关键词搜索
			if (this.keyword) {
				const kw = this.keyword.toLowerCase();
				list = list.filter(m =>
					(m.modelName || '').toLowerCase().includes(kw) ||
					(m.modelDesc || '').toLowerCase().includes(kw)
				);
			}

			return list;
		},
	},

	watch: {
		visible(val) {
			if (val && !this.loaded) {
				this.loadAllModels();
			} else if (val) {
				this.loadRecent();
			}
		},
	},

	mounted() {
		if (!this.loaded) {
			this.loadAllModels();
		}
	},

	methods: {
		// ── 数据加载 ──────────────────────────────────────
		async loadAllModels() {
			this.loading = true;
			try {
				const vendorRes = await getAiVendorListApi();
				const vendors   = (vendorRes && vendorRes.data) ? vendorRes.data : [];

				const tasks = vendors.flatMap(v => [
					getAiVendorModelListApi({ modelVendorSign: v.sign, modelType: 'text'  }),
					getAiVendorModelListApi({ modelVendorSign: v.sign, modelType: 'image' }),
				]);

				const results = await Promise.all(tasks);
				const merged  = results.flatMap(r => (r && r.data) ? r.data : []);

				const seen = new Set();
				this.allModels = merged.filter(m => {
					if (seen.has(m.guid)) return false;
					seen.add(m.guid);
					return true;
				});

				this.loaded = true;
				this.loadRecent();
			} finally {
				this.loading = false;
			}
		},

		// ── 最近使用（localStorage）─────────────────────
		loadRecent() {
			try {
				const guids = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
				this.recentModels = guids
					.map(g => this.allModels.find(m => m.guid === g))
					.filter(Boolean)
					.slice(0, MAX_RECENT);
			} catch {
				this.recentModels = [];
			}
		},

		saveRecent(model) {
			try {
				let guids = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
				guids = [model.guid, ...guids.filter(g => g !== model.guid)].slice(0, MAX_RECENT);
				localStorage.setItem(RECENT_KEY, JSON.stringify(guids));
				this.recentModels = guids
					.map(g => this.allModels.find(m => m.guid === g))
					.filter(Boolean);
			} catch {}
		},

		removeRecent(guid) {
			try {
				let guids = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
				guids = guids.filter(g => g !== guid);
				localStorage.setItem(RECENT_KEY, JSON.stringify(guids));
				this.recentModels = this.recentModels.filter(m => m.guid !== guid);
			} catch {}
		},

		// ── 交互 ──────────────────────────────────────────
		onSelect(item) {
			this.saveRecent(item);
			this.$emit('select', item);
			this.$emit('update:visible', false);
		},

		onClose() {
			this.$emit('update:visible', false);
		},

		switchScene(key) {
			this.activeScene = key;
			this.keyword = '';
		},

		onSearchInput() {
			this.activeScene = 'all';
		},

		// ── 筛选逻辑 ──────────────────────────────────────
		matchesScene(model, scene) {
			switch (scene) {
				case 'reasoning': return model.isReasoning > 0;
				case 'vision':    return model.isImageRecognition === 1;
				case 'free':      return model.usePrice === 0;
				case 'fast':      return this.isFastModel(model);
				default:          return true;
			}
		},

		sceneCount(sceneKey) {
			return this.allModels
				.filter(m => m.modelType === 'text')
				.filter(m => this.matchesScene(m, sceneKey))
				.length;
		},

		isFastModel(model) {
			return FAST_PATTERN.test((model.modelName || '') + ' ' + (model.modelSign || ''));
		},

		// ── 展示辅助 ──────────────────────────────────────
		vendorLabel(sign) {
			return VENDOR_LABELS[sign] || sign;
		},

		vendorColor(sign) {
			return VENDOR_COLORS[sign] || '#aaa';
		},

		getUseCases(model) {
			const tags = [];
			if (model.isReasoning > 0)            tags.push('逻辑推理', '数学分析');
			else if (model.isImageRecognition)    tags.push('图片理解', '视觉问答');
			else if (this.isFastModel(model))     tags.push('日常问答', '快速对话');
			else                                  tags.push('内容创作', '综合对话');
			return tags.join('、');
		},

		defaultDesc(model) {
			if (model.isReasoning === 1)          return '固定推理模式，擅长复杂逻辑、数学推导，深入分析问题。';
			if (model.isReasoning === 2)          return '支持可选深度思考，灵活兼顾速度与推理深度。';
			if (model.isImageRecognition === 1)   return '支持图片输入，可分析截图、图表、照片等视觉内容。';
			if (this.isFastModel(model))          return '响应速度快、延迟低，适合高频轻量对话场景。';
			return '通用对话模型，适合日常问答、文案创作、知识问答等场景。';
		},
	},
};
</script>

<style lang="scss">
/* 全局：el-dialog 圆角、去内边距 */
.model-select-dialog {
	border-radius: 14px !important;
	overflow: hidden;

	.el-dialog__header {
		padding: 18px 24px 14px;
		border-bottom: 1px solid #f0f0f0;
		background: #fafafa;
	}

	.el-dialog__body {
		padding: 0;
	}

	.el-dialog__headerbtn {
		top: 16px;
		right: 20px;
	}
}
</style>

<style lang="scss" scoped>
/* ── 弹窗头部 ─────────────────────────────────────── */
.dialog-header {
	display: flex;
	align-items: center;
	gap: 16px;

	.dialog-title {
		font-size: 16px;
		font-weight: 600;
		color: #1a1a1a;
		white-space: nowrap;
	}

	.search-input {
		flex: 1;
		max-width: 300px;

		::v-deep .el-input__inner {
			border-radius: 20px;
			background: #fff;
			border-color: #e0e0e0;
			font-size: 13px;

			&:focus {
				border-color: #6126F5;
				box-shadow: 0 0 0 2px rgba(97, 38, 245, 0.1);
			}
		}
	}
}

/* ── 弹窗主体 ─────────────────────────────────────── */
.dialog-body {
	padding: 16px 24px 24px;
	min-height: 460px;
}

/* ── 场景筛选 ─────────────────────────────────────── */
.scene-tabs {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-bottom: 12px;
}

.scene-tab {
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 6px 14px;
	border-radius: 20px;
	font-size: 13px;
	color: #555;
	background: #f5f5f5;
	cursor: pointer;
	transition: all 0.18s;
	user-select: none;
	border: 1.5px solid transparent;

	.scene-icon {
		font-size: 14px;
		line-height: 1;
	}

	.scene-count {
		margin-left: 3px;
		font-size: 11px;
		color: #999;
		background: #e8e8e8;
		border-radius: 8px;
		padding: 0 5px;
		line-height: 16px;
	}

	&:hover {
		background: #ede8fd;
		color: #6126F5;
	}

	&.active {
		background: #6126F5;
		color: #fff;
		border-color: #6126F5;
		box-shadow: 0 2px 8px rgba(97, 38, 245, 0.25);

		.scene-count {
			background: rgba(255,255,255,0.25);
			color: #fff;
		}
	}
}

/* ── 场景说明条 ───────────────────────────────────── */
.scene-hint {
	font-size: 12px;
	color: #6126F5;
	background: #f5f0ff;
	border: 1px solid #ddd6fe;
	border-radius: 6px;
	padding: 6px 12px;
	margin-bottom: 12px;
	line-height: 1.5;
}

/* ── 分组标题 ─────────────────────────────────────── */
.section-label {
	font-size: 12px;
	color: #999;
	margin-bottom: 8px;
	display: flex;
	align-items: center;
	gap: 5px;

	i { font-size: 12px; }

	&.mt12 { margin-top: 12px; }
}

/* ── 最近使用 ─────────────────────────────────────── */
.recent-row {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-bottom: 4px;
}

.recent-chip {
	display: flex;
	align-items: center;
	gap: 6px;
	height: 32px;
	padding: 0 12px;
	border-radius: 16px;
	border: 1.5px solid #e0e0e0;
	background: #fff;
	cursor: pointer;
	font-size: 13px;
	color: #333;
	transition: all 0.15s;
	user-select: none;

	.chip-dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	.chip-name {
		max-width: 180px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.chip-check {
		color: #6126F5;
		font-size: 13px;
	}

	.chip-remove {
		font-size: 12px;
		color: #ccc;
		opacity: 0;
		transition: opacity 0.15s, color 0.15s;
		flex-shrink: 0;
		line-height: 1;

		&:hover {
			color: #f56c6c;
		}
	}

	&:hover {
		border-color: #b39dfa;
		color: #6126F5;
		background: #faf8ff;

		.chip-remove {
			opacity: 1;
		}
	}

	&.active {
		border-color: #6126F5;
		background: #f5f0ff;
		color: #6126F5;
		font-weight: 500;
	}
}

/* ── 无结果 ───────────────────────────────────────── */
.empty-tip {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 200px;
	color: #ccc;

	i  { font-size: 36px; margin-bottom: 10px; }
	p  { font-size: 13px; color: #bbb; }
}

/* ── 模型网格 ─────────────────────────────────────── */
.model-grid {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 12px;
	max-height: 480px;
	overflow-y: auto;
	padding-right: 2px;

	&::-webkit-scrollbar { width: 4px; }
	&::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 2px; }
}

/* ── 模型卡片 ─────────────────────────────────────── */
.model-card {
	position: relative;
	border: 1.5px solid #ebebeb;
	border-radius: 10px;
	padding: 13px 14px 11px;
	cursor: pointer;
	transition: border-color 0.18s, box-shadow 0.18s, transform 0.15s;
	background: #fff;
	display: flex;
	flex-direction: column;
	gap: 6px;

	&:hover {
		border-color: #c4b5fd;
		box-shadow: 0 3px 12px rgba(97, 38, 245, 0.08);
		transform: translateY(-1px);
	}

	&.active {
		border-color: #6126F5;
		background: #faf8ff;
		box-shadow: 0 3px 14px rgba(97, 38, 245, 0.14);
	}

	/* 选中勾 */
	.check-mark {
		position: absolute;
		top: 10px;
		right: 10px;
		color: #6126F5;
		font-size: 15px;
		font-weight: 700;
	}

	/* 卡片头 */
	.card-head {
		display: flex;
		align-items: center;
		gap: 7px;
		padding-right: 20px;

		.vendor-dot {
			width: 8px;
			height: 8px;
			border-radius: 50%;
			flex-shrink: 0;
		}

		.card-name {
			font-size: 13px;
			font-weight: 600;
			color: #1a1a1a;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	/* 厂商名 */
	.card-company {
		font-size: 11px;
		color: #aaa;
		margin-top: -4px;
		padding-left: 15px;
	}

	/* 能力标签 */
	.cap-row {
		display: flex;
		flex-wrap: wrap;
		gap: 4px;
		min-height: 20px;

		.cap {
			font-size: 10px;
			padding: 1px 6px;
			border-radius: 4px;
			line-height: 18px;
			white-space: nowrap;

			&.cap-reasoning1 {
				background: #fff7e6;
				color: #d46b08;
				border: 1px solid #ffd591;
			}

			&.cap-reasoning2 {
				background: #f9f0ff;
				color: #722ed1;
				border: 1px solid #d3adf7;
			}

			&.cap-vision {
				background: #e6f7ff;
				color: #0958d9;
				border: 1px solid #91caff;
			}

			&.cap-fast {
				background: #fffbe6;
				color: #ad6800;
				border: 1px solid #ffe58f;
			}

			&.cap-free {
				background: #f6ffed;
				color: #389e0d;
				border: 1px solid #b7eb8f;
			}

		}
	}

	/* 适合场景 */
	.card-usecase {
		font-size: 11px;
		color: #6126F5;
		background: #f5f0ff;
		border-radius: 4px;
		padding: 3px 8px;

		span {
			color: #aaa;
			font-size: 10px;
		}
	}

	/* 描述 */
	.card-desc {
		font-size: 12px;
		color: #777;
		line-height: 1.6;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		flex: 1;
	}

	/* 底部价格 */
	.card-footer {
		border-top: 1px solid #f0f0f0;
		padding-top: 7px;
		margin-top: auto;

		.price-free {
			font-size: 11px;
			color: #389e0d;
			i { margin-right: 2px; }
		}

		.price-paid {
			font-size: 11px;
			color: #888;
			i { margin-right: 2px; }
		}
	}
}
</style>
